/**
 * Zugriff auf die API.
 *
 * Das Access-Token liegt bewusst nur im Arbeitsspeicher dieses Moduls, nicht im
 * localStorage: Ein Token im localStorage ueberlebt jeden XSS-Fund. Der Preis ist,
 * dass ein Neuladen der Seite abmeldet -- fuer eine Testoberflaeche der richtige
 * Tausch. Das Refresh-Token liegt ohnehin im HttpOnly-Cookie und ist fuer
 * JavaScript unerreichbar.
 */

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/** Fehler mit dem strukturierten Code der API, damit die Oberflaeche unterscheiden kann. */
export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (accessToken !== null) headers.set('Authorization', `Bearer ${accessToken}`);
  if (init.json !== undefined) headers.set('Content-Type', 'application/json');

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: 'same-origin',
    ...(init.json !== undefined ? { body: JSON.stringify(init.json) } : {}),
  });

  if (response.status === 204) return undefined as T;

  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (body ?? {}) as { error?: string; message?: string; details?: unknown };
    throw new ApiRequestError(
      error.error ?? 'internal_error',
      error.message ?? `Anfrage fehlgeschlagen (HTTP ${response.status})`,
      response.status,
      error.details,
    );
  }
  return body as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, json?: unknown) =>
    request<T>(path, { method: 'POST', ...(json !== undefined ? { json } : {}) }),
  patch: <T>(path: string, json: unknown) => request<T>(path, { method: 'PATCH', json }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Datei-Upload als multipart; kein JSON-Header, den setzt der Browser mit Boundary. */
  upload: async <T>(path: string, form: FormData): Promise<T> => {
    const headers = new Headers();
    if (accessToken !== null) headers.set('Authorization', `Bearer ${accessToken}`);
    const response = await fetch(path, {
      method: 'POST',
      headers,
      body: form,
      credentials: 'same-origin',
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const error = (body ?? {}) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      throw new ApiRequestError(
        error.error ?? 'internal_error',
        error.message ?? `HTTP ${response.status}`,
        response.status,
        error.details,
      );
    }
    return body as T;
  },
};

/**
 * Laedt eine Binaerdatei und oeffnet sie in einem neuen Tab.
 *
 * Ein gewoehnlicher `<a href>` funktioniert hier **nicht**: Das Access-Token liegt
 * bewusst nur im Arbeitsspeicher, und ein Link sendet keinen `Authorization`-Header.
 * Das Token stattdessen in die URL zu schreiben waere die schlechtere Loesung -- es
 * landete in Browserverlauf, Serverprotokoll und Referrer.
 */
export async function openAuthenticatedFile(path: string): Promise<void> {
  const headers = new Headers();
  if (accessToken !== null) headers.set('Authorization', `Bearer ${accessToken}`);

  const response = await fetch(path, { headers, credentials: 'same-origin' });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const error = (body ?? {}) as { error?: string; message?: string };
    throw new ApiRequestError(
      error.error ?? 'internal_error',
      error.message ?? `Datei nicht abrufbar (HTTP ${response.status})`,
      response.status,
    );
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  // Freigeben, sobald der neue Tab die Daten gelesen hat.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
